import React, { useState, useEffect, useMemo } from 'react';
import { read, utils } from 'xlsx';
import { Button } from 'reactstrap';
import { useQuery, useMutation } from '@tanstack/react-query';
import _ from 'lodash';
import { toISOString } from 'helpers/date';
import bulkUploadTemplateFile from 'assets/files/bulk-upload-template.xlsx';
import { createMultipleUserSaved as _createMultipleUserSaved } from 'services/user-saved';
import { listUserSavedReturns as _listUserSavedReturns } from 'services/user-saved-returns';
import ImportDataTable from './ImportDataTable';

function checkMissingNumber(arr) {
  if (arr.length < 2) {
    return -1;
  }

  let expected = arr[0];
  for (let i = 1; i < arr.length; i++) {
    if (arr[i] !== expected + 1) {
      return expected + 1;
    }
    expected = arr[i];
  }
  return -1;
}

function ImportData({ notify, confirm, session }) {
  const fileRef = React.useRef();
  const maxItems = session?.permissions['mf-import-data-max-items'] ?? 0;

  const { mutateAsync: createMultipleUserSaved, isPending: isCreateMultipleUserSaved } = useMutation({
    mutationFn: _createMultipleUserSaved,
    onSuccess: () => {
      notify.success({ message: <div>Uploaded successfully</div> });
    },
    onError: (error) => {
      notify.danger({ message: <div>{String(error)}</div> });
    },
  });

  const handleChange = async (e) => {
    e.preventDefault();
    const file = e.target.files[0];
    const maxSize = 20 * 1024 * 1024; // 20MB in bytes

    if (file.size > maxSize) {
      alert('File size exceeds 20MB. Please choose a smaller file.');
      fileRef.current.value = null;
      return;
    }

    const cont = await file.arrayBuffer();

    const wb = read(cont);
    const ws = wb.Sheets[wb.SheetNames[0]];
    const jsa = utils.sheet_to_json(ws, { header: 1 });

    let names = [];
    let symbols = [];
    let types = [];
    let jurisdictions = [];
    let currencies = [];
    let categories = [];
    let subCategories = [];

    for (let x = 0; x <= 6; x++) {
      const row = jsa[x];
      const items = [].concat(row.slice(1));

      switch (row[0]) {
        case 'Name':
          names = items;
          break;
        case 'Symbol':
          symbols = items;
          break;
        case 'Type':
          types = items;
          break;
        case 'Jurisdiction':
          jurisdictions = items;
          break;
        case 'Currency':
          currencies = items;
          break;
        case 'Category':
          categories = items;
          break;
        case 'Sub-Category':
          subCategories = items;
          break;
      }
    }

    const validList = [];
    for (let x = 0; x < names.length; x++) {
      if (!names[x] || !currencies[x]) {
        continue; // Name and Currency are mandatory
      }

      const dates = [];
      const returns = [];
      const indexes = [];

      for (let y = 7; y < jsa.length; y++) {
        const date = jsa[y][0];
        if (!date) break;

        const ret = jsa[y][x + 1];
        if (_.isNumber(ret)) {
          dates.push(toISOString(date));
          returns.push(ret);
          indexes.push(y);
        }
      }

      if (returns.length > 0) {
        const missingIndex = checkMissingNumber(indexes);
        const missingDate = missingIndex > -1 ? jsa[missingIndex][0] : null;

        validList.push({
          name: names[x],
          currency: currencies[x],
          symbol: symbols[x],
          type: types[x],
          jurisdiction: jurisdictions[x],
          category: categories[x],
          subCategory: subCategories[x],
          dates,
          returns,
          indexes,
          missingIndex,
          missingDate,
        });
      }

      if (validList.length >= maxItems) {
        break;
      }
    }

    fileRef.current.value = null;

    for (let x = 0; x < validList.length; x++) {
      const item = validList[x];
      if (item.missingIndex > -1) {
        notify.danger({ message: <div>{`Missing value at '${item.name}': ${item.missingDate}`}</div> });
        return;
      }
    }

    await createMultipleUserSaved(validList);
  };

  const handleClick = () => {
    fileRef.current.click();
  };

  return (
    <>
      <div className="tw-w-full tw-text-center tw-mt-10">
        <div className="fileinput tw-text-center tw-p-0 tw-m-0">
          <input type="file" accept=".xlsx,.csv" onChange={handleChange} ref={fileRef} />
          <Button className="btn" color="info" onClick={() => handleClick()}>
            <i className="fa fa-upload" aria-hidden="true"></i>
            Upload My Data
          </Button>
          <a
            className="hover:tw-no-underline tw-ml-1 tw-font-semibold tw-px-[16px] tw-py-[9px] tw-border tw-border-solid tw-border-[#191970] tw-text-[#191970] tw-bg-white hover:tw-text-[#191970] focus:tw-text-[#191970]"
            target="_blank"
            href={bulkUploadTemplateFile}
          >
            <i className="fa fa-download" aria-hidden="true"></i>
            Download Template
          </a>
        </div>
      </div>
      <ImportDataTable notify={notify} confirm={confirm} />
    </>
  );
}

export default ImportData;
